import React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { getTableFieldSubfields } from "@common/actions/formats";
import {
  DatasheetTableReportFormat,
  DatasheetTableReportRow,
} from "../components/configure-table/configure-table.types";
import {
  deleteTableReportFormat,
  getTableReportFormats,
  getTableDatasheetFormats,
  saveTableReportFormat,
  setTableReportFormatDefault,
  setDefaultTableDatasheetFormat,
} from "@modules/datasheets/services/datasheet";

export type TableValue = {
  tableReportFormat?: string | null;
  dssubfields?: Array<DatasheetFieldComponentGetDto>;
  fieldSubfields?: Array<DatasheetFieldComponentOriginFieldGetDto>;
};

export type TableControllerProps = {
  onChange: (params: TableValue) => void;
  value?: TableValue;
  datasheetElement?: import("@modules/datasheets/types/datasheet-element").DatasheetElement<DatasheetFieldComponentGetDto>;
  formatId?: number;
  /** Current datasheet tab ID. Used when element has no tabid so Configure Table APIs receive the correct tab. */
  currentTabId?: string | number;
};

type TableFieldContext = {
  context: number;
  tableFieldId: number;
  tabId: number;
};

const DEFAULT_COLUMN_WIDTH = 100;

const coerceContext = (rawContext: unknown) => {
  if (typeof rawContext === "number") {
    return rawContext;
  }
  if (typeof rawContext === "string") {
    const parsed = Number(rawContext);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    if (rawContext === "IDS_CONTEXT") return 1;
    if (rawContext === "PDS_CONTEXT") return 2;
    if (rawContext === "TABLE_CONTEXT") return 8;
  }
  return 1;
};

const buildOriginSubfieldFromRow = (row: DatasheetTableReportRow): DatasheetFieldComponentOriginFieldGetDto => ({
  fieldid: row.subfield.fieldId,
  fieldname: row.subfield.fieldName,
  fieldtype: {
    typeid: 0 as any,
    typename: "",
    imageURL: row.subfield.imageURL,
    fieldClass: "",
    computed: false,
    systemType: false,
  },
  permissions: "",
  tableIndex: 0,
  defaultWidth: row.width ?? DEFAULT_COLUMN_WIDTH,
  imageURL: row.subfield.imageURL,
  fieldClass: "",
  calcField: false,
  computed: false,
  rawFieldType: 0,
  table: false,
  type: 0,
  systemField: false,
  system: false,
  subfields: undefined,
  lookupTableInfo: undefined,
  fieldnameWithPath: row.subfield.fieldName,
  required: false,
  updateColsCount: 0,
  showEditorAllowed: true,
  showUploadAllowed: false,
  remoteFieldName: undefined,
  dataSourceNodeId: 0,
  remoteTableName: "",
  remoteTableKeyField: "",
  localMatchForRemoteKeyField: undefined,
  updateFieldname: row.subfield.fieldName,
  autoEditOnClick: false,
  systemDateField: false,
  remoteTableRowIdentifierFieldname: "",
  fieldcontext: "",
  tableSubField: false,
  remoteField: false,
  confirmEntry: false,
  hyperlinkfield: false,
  imageField: false,
  id: row.subfield.fieldId,
});

const buildRowFromOrigin = (field: DatasheetFieldComponentOriginFieldGetDto, order: number): DatasheetTableReportRow => ({
  order,
  width: field.defaultWidth || DEFAULT_COLUMN_WIDTH,
  subfield: {
    fieldId: field.fieldid,
    fieldName: field.fieldname,
    imageURL: field.imageURL || field.fieldtype?.imageURL || "",
  },
});

const normalizeRows = (rows: Array<DatasheetTableReportRow>) => {
  return rows.map((row) => ({
    ...row,
    order: Number(row.order) || 0,
  }));
};

const resolveTableFieldContext = (
  datasheetElement?: import("@modules/datasheets/types/datasheet-element").DatasheetElement<DatasheetFieldComponentGetDto>,
  currentTabId?: string | number
): TableFieldContext | null => {
  if (!datasheetElement) return null;
  const field = datasheetElement.original.field;
  const tableFieldId = field.fieldid ?? field.id;
  if (!tableFieldId) return null;
  const rawTabId = (datasheetElement.original as { tabid?: number; tabId?: number }).tabid
    ?? (datasheetElement.original as { tabid?: number; tabId?: number }).tabId;
  const parsedCurrentTabId = currentTabId != null
    ? (typeof currentTabId === "number" ? currentTabId : parseInt(String(currentTabId), 10))
    : 0;
  const tabId = parsedCurrentTabId > 0
    ? parsedCurrentTabId
    : (rawTabId !== undefined && rawTabId !== null && rawTabId !== 0 ? Number(rawTabId) : 0);
  const context = coerceContext(field.fieldcontext);
  return { context, tableFieldId, tabId };
};

export const useTableFieldController = (props: TableControllerProps) => {
  const { onChange, datasheetElement, formatId, currentTabId } = props;
  const defaultTableDatasheetFormatId: number | undefined =
    (datasheetElement?.original as any)?.defaultTableDatasheetFormatId ??
    (datasheetElement?.original?.field as any)?.defaultTableDatasheetFormatId ??
    undefined;
  // Derive a *stable* table context so that purely visual changes (like width)
  // do not cause a new object identity and re-trigger effects that reload
  // formats from the server.
  const tableFieldContext = React.useMemo(() => {
    return resolveTableFieldContext(datasheetElement, currentTabId);
  }, [
    // Only re-compute when the identifying primitives change
    datasheetElement?.original?.field?.fieldid,
    datasheetElement?.original?.field?.fieldcontext,
    (datasheetElement?.original as { tabid?: number } | undefined)?.tabid,
    (datasheetElement?.original as { tabId?: number } | undefined)?.tabId,
    currentTabId,
  ]);

  const [configureTableOpened, setConfigureTableOpened] = React.useState(false);
  const [formats, setFormats] = React.useState<Array<DatasheetTableReportFormat>>([]);
  const [tableFormats, setTableFormats] = React.useState<Array<DatasheetTableReportRow>>([]);
  const [defaultTableFormats, setDefaultTableFormats] = React.useState<Array<DatasheetTableReportRow>>([]);
  const [selectedDropdownFormatId, setSelectedDropdownFormatId] = React.useState<number | undefined>(undefined);
  const [tableDatasheetFormats, setTableDatasheetFormats] = React.useState<
    Array<{ id: number; name: string }>
  >([]);
  const [selectedTableDatasheetFormatId, setSelectedTableDatasheetFormatId] = React.useState<
    number | undefined
  >(undefined);

  // Ensure that when the active table field changes (for example, when user
  // switches selection between different table elements or tabs), the
  // "Default Table Datasheet" dropdown is re-initialized for that specific
  // table instead of reusing the previous table's selection.
  React.useEffect(() => {
    setSelectedTableDatasheetFormatId(undefined);
  }, [
    tableFieldContext?.tableFieldId,
    tableFieldContext?.tabId,
    formatId,
    defaultTableDatasheetFormatId,
  ]);

  const applyRowsToElement = React.useCallback((rows: Array<DatasheetTableReportRow>, reportName?: string | null) => {
    if (!datasheetElement) return;

    const normalizedRows = normalizeRows(rows);
    const orderedRows = normalizedRows
      .filter((row) => row.order > 0)
      .sort((a, b) => Number(a.order) - Number(b.order));

    const originSubfields = datasheetElement.original.field.subfields || [];
    const originById = new Map(originSubfields.map((field) => [field.fieldid, field]));

    const allOriginSubfields = normalizedRows.map((row) => {
      return originById.get(row.subfield.fieldId) || buildOriginSubfieldFromRow(row);
    });

    const dssubfields = orderedRows.map((row) => {
      const field = originById.get(row.subfield.fieldId) || buildOriginSubfieldFromRow(row);
      return {
        id: field.fieldid,
        field,
        width: row.width ?? field.defaultWidth ?? DEFAULT_COLUMN_WIDTH,
        height: 0,
        x: 0,
        y: 0,
      } as DatasheetFieldComponentGetDto;
    });

    onChange({
      tableReportFormat: reportName ?? null,
      dssubfields,
      fieldSubfields: allOriginSubfields,
    });
  }, [datasheetElement, onChange]);

  const buildDefaultRows = React.useCallback(async () => {
    const existingSubfields = datasheetElement?.original.field.subfields || [];
    if (existingSubfields.length > 0) {
      return existingSubfields.map((field, index) => buildRowFromOrigin(field, index + 1));
    }

    if (tableFieldContext) {
      const fetchedSubfields = await getTableFieldSubfields({
        fieldId: tableFieldContext.tableFieldId,
        context: tableFieldContext.context,
      });
      return fetchedSubfields.map((field, index) =>
        buildRowFromOrigin(
          {
            fieldid: field.id,
            fieldname: field.name,
            fieldtype: {
              typeid: field.type,
              typename: "",
              imageURL: field.imageURL,
              fieldClass: "",
              computed: false,
              systemType: field.system,
            },
            permissions: "",
            tableIndex: 0,
            defaultWidth: DEFAULT_COLUMN_WIDTH,
            imageURL: field.imageURL,
            fieldClass: "",
            calcField: false,
            computed: false,
            rawFieldType: field.type,
            table: field.type === 8,
            type: field.type,
            systemField: field.system,
            system: field.system,
            subfields: undefined,
            lookupTableInfo: undefined,
            fieldnameWithPath: field.fullPath || field.name,
            required: false,
            updateColsCount: 0,
            showEditorAllowed: true,
            showUploadAllowed: false,
            remoteFieldName: undefined,
            dataSourceNodeId: 0,
            remoteTableName: "",
            remoteTableKeyField: "",
            localMatchForRemoteKeyField: undefined,
            updateFieldname: field.name,
            autoEditOnClick: false,
            systemDateField: false,
            remoteTableRowIdentifierFieldname: "",
            fieldcontext: String(field.context),
            tableSubField: false,
            remoteField: false,
            confirmEntry: false,
            hyperlinkfield: false,
            imageField: false,
            id: field.id,
          },
          index + 1
        )
      );
    }

    return [];
  }, [datasheetElement, tableFieldContext]);

  const refreshFormats = React.useCallback(async () => {
    if (!tableFieldContext || !formatId) {
      return [];
    }
    const fetchedFormats = await getTableReportFormats({
      context: tableFieldContext.context,
      dsFormatId: formatId,
      tabId: tableFieldContext.tabId,
      tableFieldId: tableFieldContext.tableFieldId,
    });
    setFormats(fetchedFormats);
    return fetchedFormats;
  }, [tableFieldContext, formatId]);

  const refreshTableDatasheetFormats = React.useCallback(async () => {
    if (!tableFieldContext) {
      return [];
    }
    const fetched = await getTableDatasheetFormats({
      context: tableFieldContext.context,
      tableFieldId: tableFieldContext.tableFieldId,
    });
    setTableDatasheetFormats(fetched);
    return fetched;
  }, [tableFieldContext]);

  React.useEffect(() => {
    if (!tableFieldContext) {
      return;
    }
    // For default dropdown we only need TDS formats; table report formats
    // are fetched lazily when Configure Table is opened.
    refreshTableDatasheetFormats().catch((err) => {
      console.error(err);
    });
  }, [tableFieldContext, refreshTableDatasheetFormats]);

  React.useEffect(() => {
    if (
      !defaultTableDatasheetFormatId ||
      defaultTableDatasheetFormatId <= 0 ||
      !tableDatasheetFormats.length
    ) {
      return;
    }
    if (selectedTableDatasheetFormatId != null) {
      return;
    }
    const exists = tableDatasheetFormats.some(
      (format) => format.id === defaultTableDatasheetFormatId
    );
    if (exists) {
      setSelectedTableDatasheetFormatId(defaultTableDatasheetFormatId);
    }
  }, [
    defaultTableDatasheetFormatId,
    tableDatasheetFormats,
    selectedTableDatasheetFormatId,
  ]);

  const openConfigureTable = React.useCallback(async () => {
    setConfigureTableOpened(true);
    try {
      const fetchedFormats = await refreshFormats();
      const fallbackRows = await buildDefaultRows();
      setDefaultTableFormats(fallbackRows);

      const tableReportFormatName = datasheetElement?.original.tableReportFormat ?? null;
      const defaultFormat = fetchedFormats.find((format) => format.isDefault);
      const matchedFormat = fetchedFormats.find((format) => format.reportName === tableReportFormatName);
      const activeFormat = matchedFormat ?? defaultFormat;

      const activeRows = activeFormat?.rows?.length ? activeFormat.rows : fallbackRows;
      setTableFormats(activeRows);
      setSelectedDropdownFormatId(activeFormat?.reportNumber);
    } catch (err) {
      console.error(err);
    }
  }, [buildDefaultRows, datasheetElement, refreshFormats]);

  const closeConfigureTable = React.useCallback(() => {
    setConfigureTableOpened(false);
  }, []);

  const handleSubmitConfigureTable = React.useCallback(
    async (params: {
      formats: Array<DatasheetTableReportRow>;
      selectedDropdownFormatId?: DatasheetTableReportFormat["reportNumber"];
      formatName: string;
    }) => {
      if (!tableFieldContext || !formatId) {
        return;
      }

      const normalizedRows = normalizeRows(params.formats);
      const payload: DatasheetTableReportFormat = {
        reportNumber: 0,
        reportName: params.formatName,
        isDefault: false,
        rows: normalizedRows,
      };

      const response = await saveTableReportFormat({
        context: tableFieldContext.context,
        dsFormatId: formatId,
        tabId: tableFieldContext.tabId,
        tableFieldId: tableFieldContext.tableFieldId,
        format: payload,
      });

      const refreshed = await refreshFormats();
      const savedFormat = refreshed.find((format) => format.reportName === params.formatName);
      applyRowsToElement(normalizedRows, params.formatName);
      setSelectedDropdownFormatId(savedFormat?.reportNumber);
      closeConfigureTable();
      return response;
    },
    [applyRowsToElement, closeConfigureTable, formatId, refreshFormats, tableFieldContext]
  );

  const checkFormatNameExists = React.useCallback(
    (formatName: string) =>
      Promise.resolve(formats.some((format) => format.reportName.toLowerCase() === formatName.toLowerCase())),
    [formats]
  );

  const handleDeleteFormat = React.useCallback(
    async (formatIdToDelete: number) => {
      if (!tableFieldContext) {
        return;
      }
      const response = await deleteTableReportFormat({
        context: tableFieldContext.context,
        formatId: formatIdToDelete,
      });
      await refreshFormats();
      return response;
    },
    [refreshFormats, tableFieldContext]
  );

  const handleSetDefaultFormat = React.useCallback(
    async (reportNumber: number) => {
      if (!tableFieldContext || !formatId) {
        return;
      }
      setSelectedDropdownFormatId(reportNumber);
      const response = await setTableReportFormatDefault({
        context: tableFieldContext.context,
        dsFormatId: formatId,
        tabId: tableFieldContext.tabId,
        tableFieldId: tableFieldContext.tableFieldId,
        reportNumber,
      });
      const refreshed = await refreshFormats();
      const defaultFormat = refreshed.find((format) => format.reportNumber === reportNumber);
      if (defaultFormat?.rows) {
        applyRowsToElement(defaultFormat.rows, defaultFormat.reportName);
      }
      return response;
    },
    [applyRowsToElement, formatId, refreshFormats, tableFieldContext]
  );

  const handleSetDefaultTableDatasheet = React.useCallback(
    async (formatIdToSet: number) => {
      if (!tableFieldContext || !formatId) {
        return;
      }
      // Reflect the new default selection immediately in local state so that
      // the "Default Table Datasheet" dropdown shows the chosen value.
      setSelectedTableDatasheetFormatId(formatIdToSet);

      // Keep the in-memory datasheet element in sync so that if the
      // settings panel is re-mounted or the element is re-selected,
      // the newly chosen default is treated as the element's default.
      if (datasheetElement?.original) {
        (datasheetElement.original as any).defaultTableDatasheetFormatId = formatIdToSet;
        if ((datasheetElement.original as any).field) {
          (datasheetElement.original as any).field.defaultTableDatasheetFormatId = formatIdToSet;
        }
      }

      return setDefaultTableDatasheetFormat({
        context: tableFieldContext.context,
        tableFieldId: tableFieldContext.tableFieldId,
        parentDsFormatId: formatId,
        parentTabId: tableFieldContext.tabId,
        formatId: formatIdToSet,
      }).catch((err) => {
        console.error(err);
      });
    },
    [datasheetElement, formatId, tableFieldContext]
  );

  const handleSelectedFormatChange = React.useCallback(
    (format?: DatasheetTableReportFormat) => {
      setSelectedDropdownFormatId(format?.reportNumber);
      if (format?.rows) {
        applyRowsToElement(format.rows, format.reportName);
        return;
      }
      if (defaultTableFormats.length > 0) {
        applyRowsToElement(defaultTableFormats, null);
      }
    },
    [applyRowsToElement, defaultTableFormats]
  );

  const handleTableRowsChange = React.useCallback(
    (rows: Array<DatasheetTableReportRow>) => {
      const selectedFormat = selectedDropdownFormatId
        ? formats.find((format) => format.reportNumber === selectedDropdownFormatId)
        : undefined;
      setTableFormats(rows);
      applyRowsToElement(rows, selectedFormat?.reportName ?? null);
    },
    [applyRowsToElement, formats, selectedDropdownFormatId]
  );

  return {
    configureTableOpened,
    closeConfigureTable,
    openConfigureTable,
    handleSubmitConfigureTable,
    tableFormats,
    defaultTableFormats,
    checkFormatNameExists,
    formats,
    onSelectedFormatChange: handleSelectedFormatChange,
    onDeleteFormat: handleDeleteFormat,
    onSetDefaultFormat: handleSetDefaultFormat,
    onSetDefaultTableDatasheet: handleSetDefaultTableDatasheet,
    onTableRowsChange: handleTableRowsChange,
    selectedDropdownFormatId,
    tableDatasheetFormats,
    selectedTableDatasheetFormatId,
  };
};
